import { useMutation } from '@tanstack/react-query';
import { RefreshCw, X } from 'lucide-react';
import { useId, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { rulesHref } from '../lib/nav';
import { RECALC_LABEL, type RecalcNoticeView, moreOf, sayRecalc, sideText } from '../lib/recalc';
import { useToast } from './ErrorToast';
import { IconButton } from './ui';

/*
 * Плашка пересчёта в табеле (004, US3, T084; contracts/ui.md, «Табель»; FR-042; data-model Г.6): Callout `info` над
 * таблицей — «Часы пересчитаны по правилам, которые действуют с {дата}: изменились N дней у M
 * человек». «Что изменилось» раскрывает
 * изменения версии словами, «Почему меняем» и «Что решили»; «Какие дни» — список дней этого табеля с переходом к дню,
 * дни с «правка устарела» отмечены. «Ещё N пересчётов с {дата}» — ссылка на историю правил. Крестик закрывает плашку
 * для себя (`POST …/recalc-notice/dismiss`). Кому видна плашка, решает сервер. Часы — числа сервера.
 */

const LINK =
  'inline-flex items-center text-[13px] font-medium text-accent hover:underline max-sm:min-h-10 max-sm:h-10';

export function RecalcNotice({
  timesheetId,
  notice,
  nameOf,
  onOpenDay,
  onDismissed,
}: {
  timesheetId: number;
  notice: RecalcNoticeView;
  /** ФИО человека из строк табеля. */
  nameOf: (personId: string) => string;
  /** Переход к дню: карточка дня в правой панели. */
  onOpenDay: (personId: string, date: string) => void;
  /** Плашка закрыта: страница перечитывает табель. */
  onDismissed?: () => void;
}) {
  const ids = useId();
  const { showError } = useToast();
  const [open, setOpen] = useState<'what' | 'days' | null>(null);
  const [hidden, setHidden] = useState<number | null>(null);
  const dismiss = useMutation({
    mutationFn: () => api.dismissRecalc(timesheetId, notice.jobId),
    onMutate: () => setHidden(notice.jobId),
    onSuccess: () => onDismissed?.(),
    onError: (e) => {
      setHidden(null);
      showError(e, { screen: 'Timesheet', action: 'dismiss recalc notice', retry: () => dismiss.mutate() });
    },
  });
  if (hidden === notice.jobId) return null;

  const more = moreOf(notice);
  const toggle = (what: 'what' | 'days') => setOpen((cur) => (cur === what ? null : what));
  const b = notice.basis;

  return (
    <section
      aria-label={RECALC_LABEL.region}
      className="flex items-start gap-2.5 rounded-control border border-accent/20 bg-accent-soft px-3.5 py-2.5 text-[13px] text-ink"
    >
      <RefreshCw size={16} strokeWidth={1.75} className="mt-px shrink-0 text-accent" aria-hidden />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="font-medium">{sayRecalc.notice(notice.validFrom, notice.days.length, notice.people)}</p>
        <div className="flex flex-wrap items-center gap-x-4">
          <button
            type="button"
            aria-expanded={open === 'what'}
            aria-controls={`${ids}-what`}
            onClick={() => toggle('what')}
            className={LINK}
          >
            {RECALC_LABEL.what}
          </button>
          <button
            type="button"
            aria-expanded={open === 'days'}
            aria-controls={`${ids}-days`}
            onClick={() => toggle('days')}
            className={LINK}
          >
            {RECALC_LABEL.days}
          </button>
        </div>
        {open === 'what' && (
          <div id={`${ids}-what`} className="space-y-0.5 text-ink-2">
            {notice.changes.length > 0 && (
              <ul className="list-disc space-y-0.5 pl-5">
                {notice.changes.map((c) => (
                  <li key={`${c.group}.${c.field}`}>{c.text}</li>
                ))}
              </ul>
            )}
            <p>{sayRecalc.basisKind(b.kind)}</p>
            {b.decided.trim() && <p>{sayRecalc.decided(b.decided)}</p>}
          </div>
        )}
        {open === 'days' && (
          <ul id={`${ids}-days`} aria-label={RECALC_LABEL.days} className="space-y-0.5">
            {notice.days.map((d) => (
              <li key={`${d.personId}|${d.date}`}>
                <button
                  type="button"
                  onClick={() => onOpenDay(d.personId, d.date)}
                  className="text-left text-ink hover:text-accent hover:underline max-sm:min-h-10"
                >
                  {sayRecalc.dayLine(nameOf(d.personId), d.date, sideText(d.before), sideText(d.after))}
                  {d.staleEdit && <span className="text-warn">{` · ${RECALC_LABEL.staleEdit}`}</span>}
                </button>
              </li>
            ))}
          </ul>
        )}
        {more && (
          <p className="text-ink-2">
            {sayRecalc.more(more.count, more.since)}{' '}
            <Link to={rulesHref({ view: 'history' })} className="text-accent hover:underline">
              {RECALC_LABEL.history}
            </Link>
          </p>
        )}
      </div>
      <IconButton
        icon={X}
        label={RECALC_LABEL.dismiss}
        disabled={dismiss.isPending}
        onClick={() => dismiss.mutate()}
        className="-mt-1 -mr-2 max-sm:min-h-10"
      />
    </section>
  );
}
